import { MutableRefObject, useEffect, useRef, useState, FunctionComponent } from 'react';
import { ThreeEvent, useFrame, useThree } from '@react-three/fiber';
import { useTexture } from '@react-three/drei';
import { Mesh, MeshBasicMaterial } from 'three';
import { getGeometrySize } from '@/lib/geometry';
import { useResponsive } from '@/hooks/useResponsive';
import { clampedLerp } from '@/lib/mathUtils';
import botIconTexture from '@/img/bot-icon.png';

export const ICON_ASPECT = 1.45;
const ICON_FINAL_SCALE = 1;
const ICON_FINAL_OPACITY = 0.05;
const ICON_SCALE_DURATION = 1;

interface Props {
    initialScale?: number;
    initialOpacity?: number;
    onClick?: () => void;
    onAnimationComplete?: () => void;
}

export const BotIcon: FunctionComponent<Props> = ({
    children,
    onClick,
    onAnimationComplete,
    initialScale = ICON_FINAL_SCALE, // when initial == final, don't animate scaling
    initialOpacity = ICON_FINAL_OPACITY, // when inital == final, don't animate opacity
}) => {
    const botIcon = useTexture(botIconTexture);
    const iconMaterialRef = useRef() as MutableRefObject<MeshBasicMaterial>;
    const meshRef = useRef() as MutableRefObject<Mesh>;
    const [botIconSize, setBotIconSize] = useState<[number, number]>([0, 0]);
    const { viewport } = useThree();
    const { responsiveValue } = useResponsive();
    const iconSizeFactor = responsiveValue(0.8, 0.95);
    const [animationComplete, setAnimationComplete] = useState<boolean>(false);
    const [hovered, setHovered] = useState<boolean>(false);
    const timer = useRef<number>(0);

    useFrame((_, delta) => {
        if (animationComplete) return;

        // if we are w/in 0.01 of both opacity and scale, end animation
        if (
            ICON_FINAL_SCALE - meshRef.current.scale.x < 0.001 &&
            ICON_FINAL_OPACITY - iconMaterialRef.current.opacity < 0.01
        ) {
            setAnimationComplete(true);
            return;
        }

        // decrease opacity
        if (iconMaterialRef.current) {
            iconMaterialRef.current.opacity = clampedLerp(
                iconMaterialRef.current.opacity,
                ICON_FINAL_OPACITY,
                timer.current / ICON_SCALE_DURATION
            );
        }

        // increase scale
        if (meshRef.current) {
            timer.current += delta;
            const { x, y } = meshRef.current.scale;
            meshRef.current.scale.set(
                clampedLerp(x, ICON_FINAL_SCALE, timer.current / ICON_SCALE_DURATION),
                clampedLerp(y, ICON_FINAL_SCALE, timer.current / ICON_SCALE_DURATION),
                0
            );
        }
    });

    useEffect(() => {
        if (animationComplete && onAnimationComplete) {
            onAnimationComplete();
        }
    }, [animationComplete, onAnimationComplete]);

    useEffect(() => {
        // Set the size of the onlybots icon based on the window size, trying
        // to fill whichever dimension is smallest
        setBotIconSize(getGeometrySize(viewport.width, viewport.height, ICON_ASPECT, iconSizeFactor));
    }, [viewport, iconSizeFactor]);

    const over = (e: ThreeEvent<PointerEvent>): void => {
        e.stopPropagation();
        setHovered(true);
    };
    const out = (): void => {
        setHovered(false);
    };

    useEffect(() => {
        if (hovered) {
            document.body.style.cursor = 'pointer';
        }
        return () => {
            document.body.style.cursor = 'auto';
        };
    }, [hovered]);
    return (
        <group onPointerOver={over} onPointerOut={out} onClick={onClick}>
            <mesh position={[0, 0, -1]}>
                <planeGeometry attach="geometry" args={[500, 500]} />
                <meshBasicMaterial opacity={0} transparent />
            </mesh>
            <mesh ref={meshRef} scale={[initialScale, initialScale, 0]}>
                <planeGeometry attach="geometry" args={[botIconSize[0], botIconSize[1]]} />
                <meshBasicMaterial
                    ref={iconMaterialRef}
                    color="white"
                    map={botIcon}
                    transparent
                    opacity={initialOpacity}
                />
            </mesh>
            {children}
        </group>
    );
};
